import * as DialogPrimitives from '@kobalte/core/dialog'
import { marked } from 'marked'
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  onCleanup,
  Show,
} from 'solid-js'
import { render } from 'solid-js/web'
import { parseRules, type RuleSlot } from '~/lib/parse-rules'
import { m } from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime'
import { IntroList } from './intro-list'
import { PrizeCol } from './prize-col'
import { Spinner } from './spinner'

// Per-locale rules content. Each file becomes its own chunk (dev: served as a
// standalone raw string; prod: rolled up into a separate chunk), so we only
// download the active locale's rules text when the dialog first opens.
const ruleLoaders = import.meta.glob<string>('../content/rules/*.md', {
  query: '?raw',
  import: 'default',
})

function loaderForLocale(locale: string): () => Promise<string> {
  const direct = ruleLoaders[`../content/rules/${locale}.md`]
  const fallback = ruleLoaders['../content/rules/en.md']
  return direct ?? fallback!
}

let rulesTextPromise: Promise<string> | null = null

function loadRulesText(): Promise<string> {
  if (!rulesTextPromise) {
    rulesTextPromise = loaderForLocale(getLocale())()
  }
  return rulesTextPromise
}

function SlotContent(props: { slot: RuleSlot }) {
  if (props.slot.kind === 'intro-list') return <IntroList />
  return <PrizeCol gids={props.slot.gids} label={props.slot.label} />
}

const PROSE_CLASSES = [
  'text-[14px] leading-6 text-bakery-brown',
  '[&_h3]:mt-8 [&_h3]:text-[16px] [&_h3]:font-bold [&_h3]:text-bakery-brown-deep first:[&_h3]:mt-0',
  '[&_p]:my-2',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:marker:text-bakery-brown',
  '[&_li]:my-1 [&_li]:leading-6 [&_li]:pl-1',
  '[&_table]:mt-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[12px]',
  '[&_thead]:bg-bakery-brown-mid',
  '[&_th]:border [&_th]:border-bakery-brown-mid [&_th]:px-2 [&_th]:py-3 [&_th]:text-center [&_th]:font-bold [&_th]:text-white [&_th]:leading-tight',
  '[&_td]:border [&_td]:border-bakery-brown-soft/60 [&_td]:bg-[#e3cdab] [&_td]:px-2 [&_td]:py-3 [&_td]:text-center [&_td]:align-middle [&_td]:text-bakery-brown-deep',
].join(' ')

export function RulesDialog(props: { open: boolean; onClose: () => void }) {
  const [needsLoad, setNeedsLoad] = createSignal(false)
  createEffect(() => {
    if (props.open) setNeedsLoad(true)
  })

  const [content] = createResource(needsLoad, () => loadRulesText())

  const parsed = createMemo(() => {
    const text = content()
    return text ? parseRules(text) : null
  })
  const html = createMemo(() => {
    const p = parsed()
    if (!p) return ''
    return marked.parse(p.markdown, { gfm: true, async: false }) as string
  })

  const [bodyRef, setBodyRef] = createSignal<HTMLDivElement>()

  createEffect(() => {
    const root = bodyRef()
    const p = parsed()
    if (!root || !p) return
    html()
    const disposers: Array<() => void> = []
    for (const slot of p.slots) {
      const target = root.querySelector<HTMLElement>(`[data-slot="${slot.id}"]`)
      if (!target) continue
      target.replaceChildren()
      disposers.push(render(() => <SlotContent slot={slot} />, target))
    }
    onCleanup(() => {
      for (const dispose of disposers) dispose()
    })
  })

  return (
    <DialogPrimitives.Dialog
      open={props.open}
      onOpenChange={(isOpen) => {
        if (!isOpen) props.onClose()
      }}
    >
      <DialogPrimitives.Portal>
        <DialogPrimitives.Content class="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-bakery-cream text-bakery-brown data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0">
          <header
            class="relative flex shrink-0 items-center justify-center px-5 pb-3"
            style={{ 'padding-top': 'calc(var(--safe-area-top) + 16px)' }}
          >
            <DialogPrimitives.CloseButton
              aria-label="Close"
              class="absolute left-4 grid size-9 place-items-center rounded-full bg-white/70 text-bakery-brown shadow-sm ring-1 ring-inset ring-white/40 transition-colors active:bg-white/90"
              style={{ top: 'calc(var(--safe-area-top) + 12px)' }}
            >
              <svg
                viewBox="0 0 16 16"
                class="size-3.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                aria-hidden="true"
              >
                <path d="M3 3 L13 13 M13 3 L3 13" />
              </svg>
            </DialogPrimitives.CloseButton>
            <DialogPrimitives.Title class="text-[17px] font-semibold text-bakery-pink">
              {m.event_rule_title()}
            </DialogPrimitives.Title>
          </header>

          <Show
            when={parsed()}
            fallback={
              <div class="flex flex-1 items-center justify-center pb-[20%]">
                <Spinner class="size-10 text-bakery-brown" />
              </div>
            }
          >
            <div
              ref={setBodyRef}
              class={`flex-1 overflow-y-auto px-5 pb-[calc(var(--safe-area-bottom)+32px)] pt-3 ${PROSE_CLASSES}`}
              innerHTML={html()}
            />
          </Show>
        </DialogPrimitives.Content>
      </DialogPrimitives.Portal>
    </DialogPrimitives.Dialog>
  )
}
